/**
 * API client for the post-invest folder navigator
 * (`backend/app/postinvest/documents_api.py`).
 *
 * Follows the wasstraat client's Supabase-bearer pattern: every call sends the
 * session token, error bodies use the `{ error?: string }` shape with a Dutch
 * fallback message. The virtual pre-invest folder only exists client-side
 * (`PREINVEST_FOLDER_ID`); the backend serves it via the literal
 * `preinvest-documents` path segment.
 */

import { supabase } from '@/lib/supabase'
import type { DocumentType } from '@/features/preinvest/_shared/types'
import { PREINVEST_FOLDER_ID } from './types'
import type { Company, Document, FolderTree, PostinvestDocument } from './types'

const BASE = '/api/v1/postinvest'

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

/** Read the backend's `{ error }` body and throw it, with a Dutch fallback. */
async function throwResponseError(response: Response, fallback: string): Promise<never> {
  let message: string | undefined
  try {
    const data = (await response.json()) as { error?: string }
    message = data.error
  } catch {
    // Non-JSON body (e.g. proxy error page) — fall through to the fallback.
  }
  throw new Error(message ?? `${fallback} (${response.status})`)
}

/** GET /companies — portfolio companies (deals with a wasstraat item). */
export async function listCompanies(): Promise<Company[]> {
  const response = await fetch(`${BASE}/companies`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon portfoliobedrijven niet laden')
  }
  return (await response.json()) as Company[]
}

/** GET /companies/{dealId}/folders/tree — the folder tree plus pre-invest count. */
export async function getFolderTree(dealId: string): Promise<FolderTree> {
  const response = await fetch(`${BASE}/companies/${dealId}/folders/tree`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon mappen niet laden')
  }
  return (await response.json()) as FolderTree
}

/** POST /companies/{dealId}/folders — create a folder (root or nested). */
export async function createFolder(
  dealId: string,
  name: string,
  parentId: string | null,
): Promise<void> {
  const response = await fetch(`${BASE}/companies/${dealId}/folders`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ name, parent_id: parentId }),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon map niet aanmaken')
  }
}

/** PATCH /companies/{dealId}/folders/{folderId} — rename only. */
export async function renameFolder(
  dealId: string,
  folderId: string,
  name: string,
): Promise<void> {
  const response = await fetch(`${BASE}/companies/${dealId}/folders/${folderId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ name }),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon map niet hernoemen')
  }
}

/**
 * PATCH /companies/{dealId}/folders/{folderId} — move only. A null parent
 * means the root; the backend needs the explicit `move_to_root` flag because
 * a null `parent_id` is indistinguishable from "not provided" in JSON.
 */
export async function moveFolder(
  dealId: string,
  folderId: string,
  parentId: string | null,
): Promise<void> {
  const body = parentId === null ? { move_to_root: true } : { parent_id: parentId }
  const response = await fetch(`${BASE}/companies/${dealId}/folders/${folderId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon map niet verplaatsen')
  }
}

/** DELETE /companies/{dealId}/folders/{folderId} — folder, subfolders and contents. */
export async function deleteFolder(dealId: string, folderId: string): Promise<void> {
  const response = await fetch(`${BASE}/companies/${dealId}/folders/${folderId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  // 204 No Content — nothing to parse.
  if (!response.ok) {
    await throwResponseError(response, 'Kon map niet verwijderen')
  }
}

/**
 * Documents in one folder, newest first. The virtual pre-invest folder maps
 * to GET /companies/{dealId}/preinvest-documents; real folders to
 * GET /companies/{dealId}/folders/{folderId}/documents.
 */
export async function listFolderDocuments(
  dealId: string,
  folderId: string,
): Promise<PostinvestDocument[]> {
  const url =
    folderId === PREINVEST_FOLDER_ID
      ? `${BASE}/companies/${dealId}/preinvest-documents`
      : `${BASE}/companies/${dealId}/folders/${folderId}/documents`
  const response = await fetch(url, { headers: await authHeaders() })
  if (!response.ok) {
    await throwResponseError(response, 'Kon documenten niet laden')
  }
  return (await response.json()) as PostinvestDocument[]
}

/**
 * POST /companies/{dealId}/folders/{folderId}/documents — upload one file
 * into a folder. Sends `multipart/form-data` (fields `file` + `doc_type`);
 * the browser sets the boundary, so we drop the JSON `Content-Type`.
 */
export async function uploadToFolder(
  dealId: string,
  folderId: string,
  file: File,
  docType: DocumentType,
  noteId?: string | null,
): Promise<PostinvestDocument> {
  const headers = await authHeaders()
  delete headers['Content-Type']

  const body = new FormData()
  body.append('file', file)
  body.append('doc_type', docType)
  if (noteId) body.append('note_id', noteId)

  const response = await fetch(`${BASE}/companies/${dealId}/folders/${folderId}/documents`, {
    method: 'POST',
    headers,
    body,
  })
  if (!response.ok) {
    await throwResponseError(response, 'Uploaden mislukt')
  }
  return (await response.json()) as PostinvestDocument
}

/**
 * POST /companies/{dealId}/folders/{folderId}/documents/from-note — rendert de
 * notitie als PDF in die map. Momentopname: een latere wijziging aan de notitie
 * verandert het document niet.
 */
export async function createDocumentFromNote(
  dealId: string,
  folderId: string,
  noteId: string,
): Promise<Document> {
  const response = await fetch(
    `${BASE}/companies/${dealId}/folders/${folderId}/documents/from-note`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ note_id: noteId }),
    },
  )
  if (!response.ok) {
    await throwResponseError(response, 'Kon notitie niet als PDF opslaan')
  }
  return (await response.json()) as Document
}

/**
 * PATCH /companies/{dealId}/documents/{documentId} — rename, retype and/or
 * move a post-invest document. Only the given fields change.
 */
export async function updateDocument(
  dealId: string,
  documentId: string,
  patch: { filename?: string; doc_type?: DocumentType; folder_id?: string },
): Promise<PostinvestDocument> {
  const response = await fetch(`${BASE}/companies/${dealId}/documents/${documentId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(patch),
  })
  if (!response.ok) {
    await throwResponseError(response, 'Kon document niet bijwerken')
  }
  return (await response.json()) as PostinvestDocument
}

/** PATCH /companies/{dealId}/documents/{documentId} — move to another folder. */
export async function moveDocument(
  dealId: string,
  documentId: string,
  folderId: string,
): Promise<void> {
  await updateDocument(dealId, documentId, { folder_id: folderId })
}

/** Add `download=<filename>` to a signed URL so the browser saves it under the current name. */
export function withDownloadName(url: string, filename: string): string {
  const parsed = new URL(url, window.location.origin)
  parsed.searchParams.set('download', filename)
  return parsed.toString()
}

/** DELETE /companies/{dealId}/documents/{documentId} — soft-delete a post-invest document. */
export async function deleteDocument(dealId: string, documentId: string): Promise<void> {
  const response = await fetch(`${BASE}/companies/${dealId}/documents/${documentId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  // 204 No Content — nothing to parse.
  if (!response.ok) {
    await throwResponseError(response, 'Kon document niet verwijderen')
  }
}

/** GET /companies/{dealId}/documents/{documentId}/download — short-lived signed URL. */
export async function getDownloadUrl(dealId: string, documentId: string): Promise<string> {
  const response = await fetch(
    `${BASE}/companies/${dealId}/documents/${documentId}/download`,
    { headers: await authHeaders() },
  )
  if (!response.ok) {
    await throwResponseError(response, 'Kon downloadlink niet ophalen')
  }
  const { url } = (await response.json()) as { url: string }
  return url
}
